import { Chip, InputAdornment, TextField } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import productStyles from "../../../../assets/styles/componentStyles/productStyles/productStyles.module.scss";

interface CatalogOptionalMultipleFieldProps {
    storedValue: string;
    onChipsChange: (chips: string[]) => void;
    placeholder?: string;
    textFieldClassName?: string;
    hasInvalidUrl?: boolean;
}

const CatalogOptionalMultipleField: React.FC<CatalogOptionalMultipleFieldProps> = (props) => {
    const { storedValue, onChipsChange, placeholder, textFieldClassName, hasInvalidUrl } = props;

    const [chips, setChips] = useState<string[]>([]);
    const [inputValue, setInputValue] = useState("");
    const [focusedChipIndex, setFocusedChipIndex] = useState<number | null>(null);
    const chipContainerRef = useRef<HTMLDivElement | null>(null);
    const chipRefs = useRef<(HTMLDivElement | null)[]>([]);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const suppressInputChipDeleteRef = useRef(false);

    useEffect(() => {
        if (storedValue) {
            setChips(storedValue.split(","));
        } else {
            setChips([]);
        }
    }, [storedValue]);

    const focusChipAtIndex = (index: number) => {
        setFocusedChipIndex(index);
        setTimeout(() => {
            chipRefs.current[index]?.focus();
            chipRefs.current[index]?.scrollIntoView({
                behavior: "smooth",
                inline: "nearest",
                block: "nearest",
            });
        });
    };

    const commitChips = (newChips: string[]) => {
        setChips(newChips);
        onChipsChange(newChips);
    };

    const addChipFromInput = (chipValue: string) => {
        if (!chipValue) {
            setInputValue("");
            return;
        }

        const newChips = [...chips, chipValue];
        commitChips(newChips);
        setInputValue("");
    };

    const handleBlur = () => {
        if (inputValue.trim()) {
            addChipFromInput(inputValue.trim());
        }
    };

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter" && inputValue.trim()) {
            event.preventDefault();
            addChipFromInput(inputValue.trim());
        }

        if (event.key === "Backspace" || event.key === "Delete") {
            if (
                !suppressInputChipDeleteRef.current
                && !inputValue
                && chips.length
                && focusedChipIndex == null
            ) {
                const newChips = chips.slice(0, -1);
                commitChips(newChips);
            }
        }

        if (event.key === "ArrowLeft" && !inputValue && chips.length) {
            event.preventDefault();

            setFocusedChipIndex((prev) => {
                const newIndex = prev === null
                    ? chips.length - 1
                    : Math.max(prev - 1, 0);

                setTimeout(() => {
                    chipRefs.current[newIndex]?.focus();
                    chipRefs.current[newIndex]?.scrollIntoView({
                        behavior: "smooth",
                        inline: "nearest",
                        block: "nearest",
                    });
                });

                return newIndex;
            });
        }

        if (event.key === "ArrowRight" && focusedChipIndex !== null) {
            event.preventDefault();

            const newIndex = focusedChipIndex + 1;

            if (newIndex >= chips.length) {
                setFocusedChipIndex(null);
                inputRef.current?.focus();
                return;
            }

            focusChipAtIndex(newIndex);
        }
    };

    const handleDeleteChip = (chipIndex: number, options?: { refocusNextChip?: boolean }) => {
        suppressInputChipDeleteRef.current = true;

        const updatedChips = chips.filter((_, i) => i !== chipIndex);
        chipRefs.current = chipRefs.current.slice(0, updatedChips.length);
        commitChips(updatedChips);

        requestAnimationFrame(() => {
            suppressInputChipDeleteRef.current = false;
        });

        const shouldRefocusChip = options?.refocusNextChip ?? focusedChipIndex !== null;

        if (!shouldRefocusChip) {
            setFocusedChipIndex(null);
            return;
        }

        if (updatedChips.length === 0) {
            setFocusedChipIndex(null);

            setTimeout(() => {
                inputRef.current?.focus();
            });

            return;
        }

        const newIndex = chipIndex < updatedChips.length ? chipIndex : updatedChips.length - 1;
        focusChipAtIndex(newIndex);
    };

    useEffect(() => {
        chipContainerRef.current?.scrollTo({
            left: chipContainerRef.current.scrollWidth,
            behavior: "smooth",
        });
    }, [chips, inputValue]);

    return (
        <TextField
            fullWidth
            size="small"
            inputRef={inputRef}
            placeholder={placeholder}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleInputKeyDown}
            onBlur={handleBlur}
            sx={{ border: hasInvalidUrl ? "1px solid red" : "" }}
            InputProps={{
                startAdornment: (
                    chips?.length > 0 ?
                    <>
                        <InputAdornment position="start" className={productStyles.adornmentWrapper}>
                            <div ref={chipContainerRef} className={productStyles.chipScrollContainer}>
                                {chips?.map((chip, index) => (
                                    <Chip
                                        key={index}
                                        label={chip}
                                        size="small"
                                        onDelete={() => handleDeleteChip(index)}
                                        className={productStyles.catalogMultipleFieldChip}
                                        variant={focusedChipIndex === index ? "filled" : "outlined"}
                                        ref={(el) => (chipRefs.current[index] = el)}
                                        tabIndex={-1}
                                    />
                                ))}
                            </div>
                        </InputAdornment>
                    </> 
                    : undefined
                ),
            }}
            className={`${textFieldClassName} ${productStyles.catalogMultipleFieldInput}`}
        />
    );
};

export default CatalogOptionalMultipleField;
